// src/components/BoardPack/pages/title_page.jsx
import LoadingComponent from "@/components/common/LoadingComponent";
import React from "react";

// ─── Normalize sub_contracts to { label, children[] } ────────────────────────
function normalizeContracts(items = []) {
  return items.map((item) => {
    if (typeof item === "object" && item !== null && !Array.isArray(item)) {
      return {
        label:    String(item.name || ""),
        children: normalizeContracts(item.sub_contracts || []),
      };
    }
    if (Array.isArray(item)) {
      const [parent, ...rest] = item;
      return {
        label:    String(parent || ""),
        children: rest.map((c) => ({ label: String(c || ""), children: [] })),
      };
    }
    return { label: String(item || ""), children: [] };
  });
}

// ─── Layout constants ─────────────────────────────────────────────────────────
const CARD_W      = 230;  // card width px
const CARD_H      = 90;   // card height px (approximate)
const CARD_H_SM   = 62;   // small card height px
const CARD_GAP    = 80;   // gap between L2 cards
const CARD_GAP_SM = 48;   // gap between L3 cards
const ARROW_W     = 190;  // horizontal arrow width

// ─── Row helper style ─────────────────────────────────────────────────────────
const ROW = { display: "flex", alignItems: "center", flexShrink: 0 };

// ─── Card ─────────────────────────────────────────────────────────────────────
const BPCard = ({ title, subtitle = null, small = false }) => (
  <div style={{
    background:   "#006fc0",
    color:        "white",
    borderRadius: 12,
    padding:      small ? "12px 20px" : "20px 28px",
    textAlign:    "center",
    width:        small ? 190 : CARD_W,
    flexShrink:   0,
    boxShadow:    "0 4px 14px rgba(0,111,192,0.22)",
  }}>
    {subtitle ? (
      <>
        <div style={{ fontSize: small ? 16 : 20, fontWeight: 400, letterSpacing: "0.03em", lineHeight: 1.3 }}>{title}</div>
        <div style={{ fontSize: small ? 16 : 20, fontWeight: 400, letterSpacing: "0.03em", lineHeight: 1.3, marginTop: 4 }}>{subtitle}</div>
      </>
    ) : (
      <div style={{ fontSize: small ? 16 : 20, fontWeight: 400, letterSpacing: "0.03em", lineHeight: 1.3 }}>{title}</div>
    )}
  </div>
);

// ─── Straight horizontal arrow ────────────────────────────────────────────────
let _ahId = 0;
const ArrowH = ({ color = "#006fc0" }) => {
  const id = `ah-${++_ahId}`;
  return (
    <svg width={ARROW_W} height={24} viewBox={`0 0 ${ARROW_W} 24`} fill="none" style={{ flexShrink: 0 }}>
      <defs>
        <marker id={id} markerWidth="7" markerHeight="7" refX="6" refY="3.5" orient="auto">
          <path d="M0 0 L7 3.5 L0 7 Z" fill={color} />
        </marker>
      </defs>
      <line x1="0" y1="12" x2={ARROW_W - 10} y2="12" stroke={color} strokeWidth="1.5" markerEnd={`url(#${id})`} />
    </svg>
  );
};

// ─── Fan-out arrow: 1 source → N targets ─────────────────────────────────────
// SVG height matches the exact column of cards it points to.
// Source point = midY of SVG (= midpoint of the card column).
// Each target   = vertical center of each card slot.
let _afId = 0;
const ArrowFan = ({ count = 2, color = "#006fc0", small = false }) => {
  const cardH = small ? CARD_H_SM : CARD_H;
  const gap   = small ? CARD_GAP_SM : CARD_GAP;

  // Total height of the card column on the right
  const totalH = count * cardH + (count - 1) * gap;
  const midY   = totalH / 2;

  // Center Y of each card slot
  const slotCenters = Array.from({ length: count }, (_, i) =>
    i * (cardH + gap) + cardH / 2
  );

  const id = `af-${++_afId}`;

  return (
    <svg
      width={ARROW_W}
      height={totalH}
      viewBox={`0 0 ${ARROW_W} ${totalH}`}
      fill="none"
      // alignSelf stretch so it fills the row height, then we manually place lines
      style={{ flexShrink: 0 }}
    >
      <defs>
        <marker id={id} markerWidth="7" markerHeight="7" refX="6" refY="3.5" orient="auto">
          <path d="M0 0 L7 3.5 L0 7 Z" fill={color} />
        </marker>
      </defs>
      {slotCenters.map((ty, i) => (
        <line
          key={i}
          x1="0"             y1={midY}
          x2={ARROW_W - 10}  y2={ty}
          stroke={color}
          strokeWidth="1.5"
          markerEnd={`url(#${id})`}
        />
      ))}
    </svg>
  );
};

// ─── Column of cards with consistent gap ─────────────────────────────────────
const ColCards = ({ nodes, small = false }) => (
  <div style={{
    display:        "flex",
    flexDirection:  "column",
    gap:            small ? CARD_GAP_SM : CARD_GAP,
    alignItems:     "flex-start",
    flexShrink:     0,
  }}>
    {nodes.map((n, i) => <BPCard key={i} title={n.label} small={small} />)}
  </div>
);

// ─── Projects Table Component ────────────────────────────────────────────────
const ProjectsTable = ({ projects = [] }) => {
  if (!projects || projects.length === 0) return null;

  // Split projects into two columns
  const midPoint = Math.ceil(projects.length / 2);
  const leftColumn = projects.slice(0, midPoint);
  const rightColumn = projects.slice(midPoint);
  const rowCount = Math.max(leftColumn.length, rightColumn.length);

  const cellStyle = {
    flex: 1,
    padding: "10px 24px",
    fontSize: "14px",
    color: "#1a56db",
    textDecoration: "underline",
    fontWeight: 500,
    letterSpacing: "0.01em",
  };

  return (
    <div style={{
      width: "700px",
      borderRadius: "10px",
      overflow: "hidden",
      boxShadow: "0 2px 10px rgba(0,0,0,0.08)",
      border: "1px solid #e5e7eb",
    }}>
      <div style={{
        background: "#2E86C1",
        color: "#ffffff",
        textAlign: "center",
        fontWeight: 700,
        fontSize: "16px",
        padding: "10px 0",
      }}>
        List of Projects
      </div>
      {Array.from({ length: rowCount }).map((_, i) => (
        <div key={i} style={{
          display: "flex",
          background: i % 2 === 0 ? "#ffffff" : "#dbeafe",
        }}>
          <div style={cellStyle}>{leftColumn[i] || ""}</div>
          <div style={{ width: "2px", background: "#ffffff", flexShrink: 0 }} />
          <div style={cellStyle}>{rightColumn[i] || ""}</div>
        </div>
      ))}
    </div>
  );
};

// ─── Main TitlePage ───────────────────────────────────────────────────────────
function TitlePage({ year = "-", month = "-", page = null }) {
  const subs      = normalizeContracts(page?.sub_contracts || []);
  const hasNested = subs.some((s) => s.children.length > 0);
  const projects  = page?.projects || [];

  const pageNumber = page?.page_no;
  const [isLoading, setIsLoading] = React.useState(pageNumber === 1);
React.useEffect(() => {
    if (pageNumber === 1) {
      const timer = setTimeout(() => {
        setIsLoading(false);
      }, 1500);

      return () => clearTimeout(timer);
    }
  }, [pageNumber]);
  const renderDiagram = () => {
    // ── Case 0: no sub_contracts ──
    if (subs.length === 0) {
      return <BPCard title={page?.contract} />;
    }

    // ── Case 1: 1 flat sub-contract ──
    if (subs.length === 1 && !hasNested) {
      return (
        <div style={ROW}>
          <BPCard title={page?.contract} />
          <ArrowH />
          <BPCard title={subs[0].label} />
        </div>
      );
    }

    // ── Case 2: multiple flat sub-contracts ──
    if (!hasNested) {
      return (
        <div style={{ ...ROW, alignItems: "center" }}>
          <BPCard title={page?.contract} />
          <ArrowFan count={subs.length} />
          <ColCards nodes={subs} />
        </div>
      );
    }

    // ── Case 3: nested (2 or 3 levels) ──
    // The ArrowFan SVG height = total height of the L2 column
    // The L2 column height = sum of each "row" height
    // Each L2 row height = max(CARD_H, children_column_height)
    // We align everything using flexbox — no absolute positioning

    return (
      <div style={{ ...ROW, alignItems: "center" }}>
        {/* L1 */}
        <BPCard title={page?.contract} />

        {/* Arrow L1 → L2 */}
        <ArrowFan count={subs.length} />

        {/* L2 column */}
        <div style={{
          display:       "flex",
          flexDirection: "column",
          gap:           CARD_GAP,
          alignItems:    "flex-start",
          flexShrink:    0,
        }}>
          {subs.map((node, i) => (
            // Each L2 row: card + optional arrow + L3 column
            <div key={i} style={{ ...ROW, alignItems: "center" }}>
              <BPCard title={node.label} />
              {node.children.length > 0 && (
                <>
                  {node.children.length === 1
                    ? <ArrowH />
                    : <ArrowFan count={node.children.length} small />}
                  <ColCards nodes={node.children} small />
                </>
              )}
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <PlainScreen
      showDate={!!page?.showDate}
      dateText={`${month} - ${year}`}
      showNote={!!page?.showNote}
      noteText={`Note: ${page?.notes}`}
    >
        <div style={{ 
      display: "flex", 
      flexDirection: "column", 
      alignItems: "center",
      justifyContent: "center",
      gap: "30px",
      width: "100%",
      padding: "20px 0",
    }}>
      {/* Diagram Section */}
      <div style={{ display: "flex", justifyContent: "center" }}>
        {isLoading ? <LoadingComponent /> : renderDiagram()}
      </div>
      
      {/* Projects List Section */}
      {projects.length > 0 && (
        <div style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          marginTop: "10px",
        }}>
          <ProjectsTable projects={projects} />
        </div>
      )}
    </div>
    </PlainScreen>
  );
}

export default TitlePage;

// ─── PlainScreen ──────────────────────────────────────────────────────────────
export const PlainScreen = ({
  children,
  showDate  = false,
  dateText  = "Oct - 2025",
  showNote  = false,
  noteText  = "Note: Numbers have been rounded up to the nearest value...",
  className = "",
}) => (
  <div className={`min-h-screen flex flex-col sm:flex-row relative ${className}`}>
    {showDate && (
      <div className="absolute top-4 left-4 !border-y-2 !border-x-1 !border-[#006fc0] rounded px-10 py-2 text-[#006fc0] font-semibold text-lg z-10">
        {dateText}
      </div>
    )}
    <div className="flex-1 flex items-center justify-center" style={{zoom:"90%"}}>
      {children}
    </div>
    {showNote && (
      <div className="absolute bottom-15 left-3 text-[10px] text-gray-800">
        {noteText}
      </div>
    )}
  </div>
);

// ─── Legacy named exports (backward compatibility) ────────────────────────────
export const Card = BPCard;

export const DiagramFlowSingle = ({ leftCard, rightCards, arrowColor = "#006fc0" }) => (
  <div className="min-h-screen flex items-center justify-center">
    <div style={ROW}>
      {leftCard}
      <ArrowH color={arrowColor} />
      <div>{rightCards}</div>
    </div>
  </div>
);

export const DiagramFlow = ({ leftCard, rightCards = [], arrowColor = "#006fc0" }) => (
  <div className="min-h-screen flex items-center justify-center">
    <div style={{ ...ROW, alignItems: "center" }}>
      {leftCard}
      <ArrowFan count={rightCards.length} color={arrowColor} />
      <div style={{ display: "flex", flexDirection: "column", gap: CARD_GAP }}>
        {rightCards.map((c, i) => <div key={i}>{c}</div>)}
      </div>
    </div>
  </div>
);

export const DiagramFlowMultiLevel = ({ contractLabel, nodes = [], arrowColor = "#006fc0" }) => (
  <div className="min-h-screen flex items-center justify-center">
    <div style={{ ...ROW, alignItems: "center" }}>
      <BPCard title={contractLabel} />
      <ArrowFan count={nodes.length} color={arrowColor} />
      <div style={{ display: "flex", flexDirection: "column", gap: CARD_GAP, alignItems: "flex-start" }}>
        {nodes.map((node, i) => (
          <div key={i} style={{ ...ROW, alignItems: "center" }}>
            <BPCard title={node.label} />
            {node.children.length > 0 && (
              <>
                {node.children.length === 1
                  ? <ArrowH color={arrowColor} />
                  : <ArrowFan count={node.children.length} color={arrowColor} small />}
                <div style={{ display: "flex", flexDirection: "column", gap: CARD_GAP_SM }}>
                  {node.children.map((child, j) => (
                    <BPCard key={j} title={child.label} small />
                  ))}
                </div>
              </>
            )}
          </div>
        ))}
      </div>
    </div>
  </div>
);